import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUser,
  faPhone,
  faMapMarkerAlt,
  faMoneyBillWave,
  faEnvelope,
  faHome,
  faCity,
  faGlobeAsia,
  faMailBulk,
  faCircleExclamation,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const SELLER_STATE = "Maharashtra";
// const DELIVERY_CHARGE = 49;

const HerKartCheckout = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer);
  const { fetchCart, placeOrder: placeOrderApi } = useHerKart();

  const [cartItems, setCartItems] = useState([]);
  const [loadingCart, setLoadingCart] = useState(true);
  const [placing, setPlacing] = useState(false);

  const [form, setForm] = useState({
    name: authState?.user?.name || "",
    mobile: authState?.user?.mobile || "",
    email: authState?.user?.email || "",
    house: "",
    street: "",
    landmark: authState?.user?.area || "",
    city: authState?.user?.city || "",
    state: authState?.user?.state || "",
    pincode: authState?.user?.pinCode || "",
  });

  const [errors, setErrors] = useState({});

  // Inline UI message (replaces alert())
  const [message, setMessage] = useState(null);

  const loadMyCart = async () => {
    setLoadingCart(true);
    setMessage(null);
    try {
      const response = await fetchCart();
      // if (response?.success) {
      //   setCartItems(response.items || []);
      // }
        if (response?.success) {
          const items = response.items || [];
          // console.log("Cart Items Received:", items);   // ← Debug
          setCartItems(items);
        }
    } catch (e) {
      console.error("Cart fetch error:", e);
      setMessage({ type: "error", text: "Could not load your cart. Please try again." });
    } finally {
      setLoadingCart(false);
    }
  };

  useEffect(() => {
    loadMyCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GST split note: same state as seller → CGST + SGST; different state → IGST.
  const isIntraState = useMemo(() => {
    return (form.state || "").trim().toLowerCase() === SELLER_STATE.toLowerCase();
  }, [form.state]);

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cartItems]);

  const totalItems = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [cartItems]);

  // const delivery = cartItems.length === 0 ? 0 : DELIVERY_CHARGE;
  const delivery = cartItems.reduce((sum, item) => sum + (item.deliveryCharge || 0), 0);
  const grandTotal = subtotal + delivery;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(form.mobile)) nextErrors.mobile = "Enter a valid mobile number";
    if (!form.house.trim()) nextErrors.house = "House / Flat is required";
    if (!form.street.trim()) nextErrors.street = "Street / Area is required";
    if (!form.city.trim()) nextErrors.city = "City is required";
    if (!form.state.trim()) nextErrors.state = "State is required";
    if (!/^\d{6}$/.test(form.pincode)) nextErrors.pincode = "Enter a valid PIN code";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePlaceOrder = async () => {
    setMessage(null);

    if (cartItems.length === 0) {
      setMessage({ type: "error", text: "Your cart is empty." });
      return;
    }

    if (!validate()) {
      setMessage({ type: "error", text: "Please fix the highlighted fields before continuing." });
      return;
    }

    setPlacing(true);
    try {
      const payload = {
        items: cartItems.map((item) => ({
          productId: item.productId,
          name: item.name,
          store: item.store,
          entrepreneur: item.entrepreneur,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
        })),
        customer: {
          name: form.name,
          mobile: form.mobile,
          email: form.email,
          house: form.house,
          street: form.street,
          landmark: form.landmark,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
        },
        totalItems,
        subtotal,
        delivery,
        grandTotal,
      };

      const response = await placeOrderApi(payload);

      if (response?.success) {
        navigate("/herkart/order-success", { state: { order: response.order } });
      } else {
        setMessage({ type: "error", text: "Failed to place order. Please try again." });
      }
    } catch (e) {
      console.error("Place order error:", e);
      setMessage({ type: "error", text: "Failed to place order. Please try again." });
    } finally {
      setPlacing(false);
    }
  };

  if (loadingCart) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">Loading your cart...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex flex-row max-w-7xl items-start justify-start gap-3 px-4 py-2">
          <Link
            to="/herkart/cart"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>

          <div className="flex-1 flex flex-col items-start text-left">
            <h1 className="w-full text-left text-lg lg:text-xl font-bold">Checkout</h1>

            <p className="w-full text-left text-xs text-slate-100">Cash on Delivery</p>
          </div>

          <button
            onClick={loadMyCart}
            className="rounded-xl bg-white/10 w-fit px-2 py-1 text-xs hover:bg-white/20"
            title="Refresh cart"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-7xl px-4 pt-4">
          <div
            className={`flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon
              icon={message.type === "success" ? faCircleCheck : faCircleExclamation}
            />
            {message.text}
          </div>
        </div>
      )}

      {cartItems.length === 0 ? (
        <div className="mx-auto max-w-7xl px-4 py-16 text-center">
          <p className="text-lg text-slate-300">Your cart is empty.</p>
          <Link
            to="/herkart/products"
            className="mt-4 inline-block rounded-2xl bg-pink-600 px-6 py-1 font-semibold hover:bg-pink-500"
          >
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-2 lg:grid-cols-3">
          <div className="space-y-2 lg:col-span-2">
            <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-2">
              <h2 className="mb-2 text-xl font-bold text-cyan-200">Delivery Details</h2>

              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faUser} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faPhone} />
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                  {errors.mobile && <p className="mt-1 text-xs text-red-400">{errors.mobile}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faEnvelope} />
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faHome} />
                    House / Flat
                  </label>
                  <input
                    type="text"
                    name="house"
                    value={form.house}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                  {errors.house && <p className="mt-1 text-xs text-red-400">{errors.house}</p>}
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    Street / Area
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={form.street}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                  {errors.street && <p className="mt-1 text-xs text-red-400">{errors.street}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 text-sm text-slate-300">Landmark (Optional)</label>
                  <input
                    type="text"
                    name="landmark"
                    value={form.landmark}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faCity} />
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                  {errors.city && <p className="mt-1 text-xs text-red-400">{errors.city}</p>}
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faGlobeAsia} />
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                  {errors.state && <p className="mt-1 text-xs text-red-400">{errors.state}</p>}
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm text-slate-300">
                    <FontAwesomeIcon icon={faMailBulk} />
                    PIN Code
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    maxLength={6}
                    value={form.pincode}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                  {errors.pincode && <p className="mt-1 text-xs text-red-400">{errors.pincode}</p>}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="sticky top-24 rounded-3xl border border-white/10 bg-white/5 p-6">
              <h2 className="text-xl font-bold text-cyan-200">Order Summary</h2>

              <div className="mt-6 max-h-64 space-y-3 overflow-y-auto">
                {cartItems.map((item) => (
                  <div
                    key={item.productId}
                    className="flex items-center gap-3 rounded-xl bg-white/5 p-3"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-14 w-14 rounded-xl object-cover"
                    />
                    <div className="flex-1">
                      <h4 className="line-clamp-1 text-cyan-100 font-medium">{item.name}</h4>
                      <p className="text-xs text-slate-200">Qty {item.quantity}</p>
                      {item.remarks && (
                        <p className="text-xs text-amber-300">{item.remarks}</p>
                      )}
                    </div>
                    <div className="font-semibold text-pink-400">
                      ₹{item.price * item.quantity}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 space-y-3 border-t border-white/10 pt-5">
                <div className="flex justify-between">
                  <span className="text-slate-200">Items</span>
                  <span>{totalItems}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-200">Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-200">Delivery</span>
                  <span>₹{delivery}</span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-4 text-xl font-bold">
                  <span>Total</span>
                  <span className="text-pink-400">₹{grandTotal}</span>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faMoneyBillWave} className="text-emerald-400" />
                  <div>
                    <h4 className="font-semibold text-cyan-100">Cash on Delivery</h4>
                    <p className="text-sm text-slate-200">
                      Payment will be collected at the time of delivery.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={placing}
                className="mt-4 rounded-2xl bg-pink-600 w-fit px-2 py-1  text-lg font-semibold transition hover:bg-pink-500 disabled:opacity-60"
              >
                {placing ? "Placing Order..." : "Place Order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerKartCheckout;